import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import type { IconButtonProps } from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { recordPage } from "permissions/_views/wording/permission-wording";
import type { ComponentType } from "react";
// The pages draw Comments and Activity in this container, which has no bottom margin. The one of the
// same name in proposal-styled-components has 16px, and in a row that centres its items that put the
// lock 8px above its neighbours.
import { ButtonContainer } from "work/_views/work-page-styled-components";
import {
  CommentButton,
  CommentButtonLabel,
  HighlightCommentButton,
} from "work/proposals/_views/_shared/proposal-styled-components";

/** The caption under the lock: the short form of "Who has access", which stays the control's name. */
export const RECORD_ACCESS_CAPTION = "Access";

/**
 * The lock itself: the icon, the name "Who has access" and its tooltip, drawn with the button of the
 * row it sits in. A record's header captions it, as below; a conversation's tab row draws its icons
 * bare, so the lock sits there uncaptioned beside the member list (#6836).
 */
export function RecordAccessLock({
  onClick,
  button,
  size,
}: Readonly<{ onClick: () => void; button?: ComponentType<IconButtonProps>; size?: IconButtonProps["size"] }>) {
  const Button = button ?? HighlightCommentButton;
  return (
    <Tooltip enterDelay={600} placement="bottom-end" title={recordPage.whoHasAccess}>
      <Button
        // A caption, where there is one, is a sibling of the button, not its content.
        aria-label={recordPage.whoHasAccess}
        size={size ?? "medium"}
        color="primary"
        onClick={onClick}
      >
        <LockOutlinedIcon fontSize="medium" />
      </Button>
    </Tooltip>
  );
}

/**
 * Who has access, as a lock captioned "Access", the last icon in a record's header (#6598).
 *
 * One control for a proposal and the work agreement it becomes, so a reader moving from one to the
 * other finds it in the same place, looking the same and with the same name. A proposal put it beside
 * Comments and Activity as a lock in #6521; the work agreement drew its own, first in its header, with
 * a different icon and its caption spelled out. Reading who holds a grant moves nothing on, so it sits
 * with the other things a reader looks at rather than among the actions.
 */
export default function RecordAccessButton({ onClick }: Readonly<{ onClick: () => void }>) {
  return (
    <CommentButton data-testid="record-access-button">
      <ButtonContainer>
        <RecordAccessLock onClick={onClick} />
        <CommentButtonLabel variant="button" color="primary">
          {RECORD_ACCESS_CAPTION}
        </CommentButtonLabel>
      </ButtonContainer>
    </CommentButton>
  );
}
